import { commonAction } from '@/types/commonActions';
import { actionTypes } from './actionTypes';
import { createContext } from './createContext';
import RepairForm from '../components/RepairForm/index';
import { ServiceSlot } from '@/components/ChooseSlot';
import { address } from '@/components/Service/SubPages/ServiceRequest';
import { boolDesc, dateSelectionMethod } from '@/types/wegengage';
import { Slot } from '@/types/slot';
import { useContext } from 'react';

export type serviceRequestComponents =
  | 'pendingRequest'
  | 'bookAService'
  | 'selectRepair'
  | 'bookASlot'
  | 'serviceReqSummary'
  | 'reInstallForm'
  | 'bookServiceSuccess'
  | 'mapLocator'
  | 'ComprehensivePlans'
  | 'ComprehensiveSummary';

export type serviceRequestType =
  | 'repair'
  | 'uninstall'
  | 'reinstall'
  | 'ms'
  | 'REPAIR'
  | 'REINSTALL'
  | 'DISMANTLING'
  | '';

export type componentObj = {
  index: number;
  component: serviceRequestComponents;
  displayLabel: string;
  active: boolean;
};

export const serviceComponentMap: componentObj[] = [
  {
    index: 0,
    component: 'pendingRequest',
    displayLabel: 'Pending Request',
    active: true,
  },
  {
    index: 1,
    component: 'bookAService',
    displayLabel: 'Book a Service',
    active: false,
  },
  {
    index: 2,
    component: 'reInstallForm',
    displayLabel: 'Re-Install',
    active: false,
  },
  {
    index: 3,
    component: 'selectRepair',
    displayLabel: 'Repair',
    active: false,
  },
  {
    index: 4,
    component: 'bookASlot',
    displayLabel: 'Book a slot',
    active: false,
  },
  {
    index: 5,
    component: 'serviceReqSummary',
    displayLabel: 'Service Request Summary',
    active: false,
  },
  {
    index: 6,
    component: 'bookServiceSuccess',
    displayLabel: '',
    active: false,
  },

  {
    index: 7,
    component: 'mapLocator',
    displayLabel: '',
    active: false,
  },
  {
    index: 8,
    component: 'ComprehensivePlans',
    displayLabel: 'Comprehensive Plans',
    active: false,
  },
  {
    index: 9,
    component: 'ComprehensiveSummary',
    displayLabel: 'Comprehensive Summary',
    active: false,
  },
];

export type ServiceRequestContextType = {
  serviceComponentMap: componentObj[];
  currentComp: componentObj;
  serviceType: serviceRequestType;
  defectType: string;
  addnRemarks: string;
  serviceDate: Date;
  serviceSlot?: ServiceSlot;
  serviceDuration: string;
  address: address;
  useCurrentAddress: boolean;
  serviceRequestID: string;
  serviceReqHistoryData: serviceReqHistoryInterface;
  isRescheduleFlow: true | '';
  isCancellable: boolean;
  webengageData: {
    date_selection_method: dateSelectionMethod;
    service_request_open: boolDesc;
    scheduling_automation_flow: boolDesc;
    mandatory_service_open: boolDesc;
    current_timestamp: number;
    available_slots: string;
    current_time_vs_earliest_available_slot: number;
    time_slot_selected: string;
    date_selected: string;
    reschedule: boolDesc;
    product_selected: string;
    product_id: string;
    service_type: string;
    defect_selected: string;
    customer_type: string;
    amc_tier: string;
    amc_plan_duration: string;
    amc_plan_value: number;
    amc_plan_valid_upto: string;
    amc_plan_valid_from: string;
    mandatory_service_available: number;
    mandatory_service_availed: number;
    product_flag?: string;
    customer_code?: string;
  };
  bookAServiceTriggeredFrom?: string;
  isRouting?: boolean;
  incidentId?: string;
  workOrderId?: string;
  isFMS?: boolean;
  incidentType?: string;
  customerCode?: string;
  componentId?: string;
  servicePrice: number; // added
  isPrepaidAllowed: boolean; // added
  productId: string;
  materialId: string;
  productName: string;
  prepaidAmountCollected: string;
  rescheduleReasonCode: any;
  rescheduleReasonValue: string;
  rescheduleDescription: string;
  is_vc_tracking: boolean;
  comprehensiveServiceData?: any; // Data from comprehensive service API
  originalAmount?: number;
};

export type ServiceRequestDispatchType = {
  changeComponent: Function;
  backAction: Function;
  updateServiceType: Function;
  updateRepairType: Function;
  updateServiceDate: Function;
  updateServiceSlot: Function;
  updateAddress: Function;
  resetServiceAllocation: Function;
  updateUseCurrentAddress: Function;
  updateSRContext: Function;
  setServiceRequestID: Function;
  updateSRHistoryContext: Function;
  initWebengageContext: Function;
  setRescheduleFlow: Function;
  setIsRouting: Function;
  resetService: Function;
  updateIncidentId: Function;
  setRescheduleReason: Function;
  setComprehensiveServiceData: Function;
};

export type serviceReqData = {
  id?: number | string;
  serviceNumber: number;
  slotData: ServiceSlot;
  serviceDate: Date;
  serviceType: string;
};

export interface serviceReqHistoryInterface {
  openRequests: serviceReqData[] | [];
  closedRequests: serviceReqData[] | [];
}

export const initialState: ServiceRequestContextType = {
  serviceComponentMap: serviceComponentMap,
  currentComp: serviceComponentMap[0],
  serviceType: '',
  defectType: '',
  addnRemarks: '',
  serviceDate: new Date(),
  serviceDuration: '01:00',
  serviceSlot: undefined,
  useCurrentAddress: false,
  isCancellable: true,
  address: {
    house_number: NaN,
    address_name: '',
    city: '',
    state: '',
    postal_code: NaN,
    latitude: '',
    longitude: '',
  },
  serviceRequestID: '',
  serviceReqHistoryData: {
    openRequests: [],
    closedRequests: [],
  },
  isRescheduleFlow: '',
  isRouting: false,
  webengageData: {
    service_request_open: 'No',
    scheduling_automation_flow: 'Yes',
    mandatory_service_open: 'No',
    current_timestamp: Date.now(),
    available_slots: '',
    current_time_vs_earliest_available_slot: 0,
    time_slot_selected: '',
    date_selected: '',
    reschedule: 'No',
    date_selection_method: 'Quick View',
    product_selected: '',
    product_id: '',
    service_type: '',
    defect_selected: '',
    customer_type: '',
    amc_tier: '',
    amc_plan_duration: '',
    amc_plan_value: 0,
    amc_plan_valid_upto: '',
    amc_plan_valid_from: '',
    mandatory_service_available: 0,
    mandatory_service_availed: 0,
    customer_code: '',
  },
  isFMS: false,
  incidentType: '',
  servicePrice: 0, // added
  isPrepaidAllowed: false, // added
  productId: '',
  materialId: '',
  productName: '',
  prepaidAmountCollected: '',
  rescheduleReasonCode: '',
  rescheduleReasonValue: '',
  rescheduleDescription: '',
  is_vc_tracking: false,
  comprehensiveServiceData: null,
};

export function serviceRequestReducer(state: ServiceRequestContextType, action: commonAction) {
  switch (action.type) {
    case actionTypes.CHANGE_SERVICE_COMPONENT: {
      return {
        ...state,
        currentComp: action.payload.currentComponent,
      };
    }
    case actionTypes.UPDATE_SERVICE_TYPE: {
      return {
        ...state,
        serviceType: action.payload.serviceType,
      };
    }
    case actionTypes.SERVICE_ALLOCATION.UPDATE_DEFECT_TYPE: {
      return {
        ...state,
        defectType: action.payload.defectType,
        addnRemarks: action.payload.addnRemarks,
      };
    }
    case actionTypes.SERVICE_ALLOCATION.UPDATE_SERVICE_DATE: {
      return {
        ...state,
        serviceDate: action.payload.serviceDate,
      };
    }
    case actionTypes.SERVICE_ALLOCATION.UPDATE_SERVICE_SLOT: {
      return {
        ...state,
        serviceSlot: action.payload.serviceSlot,
      };
    }

    case actionTypes.UPDATE_ADDRESS: {
      return {
        ...state,
        address: action.payload.address,
      };
    }

    case actionTypes.SERVICE_ALLOCATION.RESET_STATE: {
      return {
        ...initialState,
        webengageData: {
          ...state.webengageData,
        },
        serviceReqHistoryData: {
          ...state.serviceReqHistoryData,
        },
        servicePrice: 0, // reset to default
        isPrepaidAllowed: false, // reset to default
      };
    }

    case actionTypes.UPDATE_USE_CURRENT_ADDRESS: {
      return {
        ...state,
        useCurrentAddress: action.payload,
      };
    }

    case actionTypes.SERVICE_ALLOCATION.INTIALIZE_CONTEXT: {
      return {
        ...state,
        ...action.payload,
      };
    }

    case actionTypes.SERVICE_ALLOCATION.INITIALIZE_REQ_HISTORY: {
      return {
        ...state,
        serviceReqHistoryData: action.payload,
      };
    }

    case actionTypes.SET_SERVICE_REQUEST_ID: {
      return {
        ...state,
        serviceRequestID: action.payload,
      };
    }

    case actionTypes.SERVICE_ALLOCATION.INITIALIZE_SR_WEBENGAGE: {
      return {
        ...state,
        webengageData: {
          ...state.webengageData,
          ...action.payload,
        },
      };
    }

    case actionTypes.SET_BOOK_A_SERVICE_TRIGGERED_FROM: {
      return {
        ...state,
        bookAServiceTriggeredFrom: action.payload,
      };
    }

    case actionTypes.SET_RESCHEDULE_FLOW: {
      return {
        ...state,
        isRescheduleFlow: action.payload,
      };
    }

    /* istanbul ignore next */
    case actionTypes.SET_IS_ROUTING: {
      return {
        ...state,
        isRouting: action.payload,
      };
    }

    case actionTypes.SET_INCIDENT_ID: {
      return {
        ...state,
        incidentId: action.payload,
      };
    }

    case actionTypes.SET_RESCHEDULE_REASON: {
      const { reasonCode, reasonValue, description } = action.payload || {};
      return {
        ...state,
        rescheduleReasonCode: reasonCode,
        rescheduleReasonValue: reasonValue,
        rescheduleDescription: description,
      };
    }

    case 'RESET': {
      return {
        ...initialState,
        servicePrice: 0, // reset to default
        isPrepaidAllowed: false, // reset to default
      };
    }

    case actionTypes.SET_COMPREHENSIVE_SERVICE_DATA: {
      return {
        ...state,
        comprehensiveServiceData: action.payload,
      };
    }

    default:
      throw new Error(`Unsupported type: ${action.type}`);
  }
}

const changeComponent = (dispatch: any) => (component: string, triggeredFrom?: string) => {
  const _comp = serviceComponentMap.find((el) => el.component === component);
  const _updatedSCMap = serviceComponentMap.map((el) => {
    if (el.component === component) {
      el.active = true;
    } else {
      el.active = false;
    }
    return el;
  });
  dispatch({
    type: actionTypes.CHANGE_SERVICE_COMPONENT,
    payload: { currentComponent: _comp, serviceComponentMap: _updatedSCMap },
  });
  if (component === 'bookAService') {
    dispatch({
      type: actionTypes.SET_BOOK_A_SERVICE_TRIGGERED_FROM,
      payload: triggeredFrom,
    });
  }
};

type RepairForm = {
  defectType: string;
  addnRemarks: string;
};

const updateServiceType = (dispatch: any) => (serviceType: string) => {
  dispatch({
    type: actionTypes.UPDATE_SERVICE_TYPE,
    payload: { serviceType },
  });
};

const updateRepairType =
  (dispatch: any) =>
  ({ defectType, addnRemarks }: RepairForm) => {
    dispatch({
      type: actionTypes.SERVICE_ALLOCATION.UPDATE_DEFECT_TYPE,
      payload: { defectType, addnRemarks },
    });
  };

const updateAddress = (dispatch: any) => (address: address) => {
  dispatch({
    type: actionTypes.UPDATE_ADDRESS,
    payload: { address },
  });
};

const updateServiceDate = (dispatch: any) => (serviceDate: Date) => {
  dispatch({
    type: actionTypes.SERVICE_ALLOCATION.UPDATE_SERVICE_DATE,
    payload: { serviceDate },
  });
};

const updateServiceSlot = (dispatch: any) => (serviceSlot: Slot) => {
  dispatch({
    type: actionTypes.SERVICE_ALLOCATION.UPDATE_SERVICE_SLOT,
    payload: { serviceSlot },
  });
};

const resetServiceAllocation = (dispatch: any) => () => {
  dispatch({
    type: actionTypes.SERVICE_ALLOCATION.RESET_STATE,
    payload: {},
  });
};
const updateIncidentId = (dispatch: any) => (incidentId: string) => {
  dispatch({
    type: actionTypes.SET_INCIDENT_ID,
    payload: incidentId,
  });
};

const backAction = (dispatch: any) => (state: ServiceRequestContextType) => {
  const { serviceComponentMap, currentComp, serviceType } = state;
  let _currentComp = currentComp;
  const { index } = currentComp;
  const _updatedSCMap = serviceComponentMap.map((el) => {
    if (serviceType === 'uninstall') {
      if (el.index == index - 3 && currentComp.component === 'bookASlot') {
        el.active = true;
        _currentComp = el;
      } else if (el.index === index - 1 && currentComp.component !== 'bookASlot') {
        el.active = true;
        _currentComp = el;
      } else {
        el.active = false;
      }
    } else if (serviceType === 'repair') {
      if (el.index == index - 1 && currentComp.component === 'bookASlot') {
        el.active = true;
        _currentComp = el;
      } else if (el.index == index - 2 && currentComp.component === 'selectRepair') {
        el.active = true;
        _currentComp = el;
      } else if (el.index == index - 1 && currentComp.component !== 'selectRepair') {
        el.active = true;
        _currentComp = el;
      } else {
        el.active = false;
      }
    } else if (serviceType === 'reinstall') {
      if (el.index == index - 2 && currentComp.component === 'bookASlot') {
        el.active = true;
        _currentComp = el;
      } else if (el.index == index - 1 && currentComp.component !== 'bookASlot') {
        el.active = true;
        _currentComp = el;
      } else {
        el.active = false;
      }
    } else {
      if (el.index === index - 1) {
        el.active = true;
        _currentComp = el;
      } else {
        el.active = false;
      }
    }
    return el;
  });

  dispatch({
    type: actionTypes.CHANGE_SERVICE_COMPONENT,
    payload: { currentComponent: _currentComp, serviceComponentMap: _updatedSCMap },
  });
};

const updateUseCurrentAddress = (dispatch: any) => (useCurrentAddress: Slot) => {
  dispatch({
    type: actionTypes.UPDATE_USE_CURRENT_ADDRESS,
    payload: useCurrentAddress,
  });
};

const updateSRContext = (dispatch: any) => (obj: any) => {
  dispatch({
    type: actionTypes.SERVICE_ALLOCATION.INTIALIZE_CONTEXT,
    payload: obj,
  });
};

const updateSRHistoryContext = (dispatch: any) => (obj: any) => {
  dispatch({
    type: actionTypes.SERVICE_ALLOCATION.INITIALIZE_REQ_HISTORY,
    payload: obj,
  });
};

const setServiceRequestID = (dispatch: any) => (id: string) => {
  dispatch({
    type: actionTypes.SET_SERVICE_REQUEST_ID,
    payload: id,
  });
};

const initWebengageContext = (dispatch: any) => (webengageData: any) => {
  dispatch({
    type: actionTypes.SERVICE_ALLOCATION.INITIALIZE_SR_WEBENGAGE,
    payload: webengageData,
  });
};

const setRescheduleFlow = (dispatch: any) => (isRescheduleFlow: true | '') => {
  dispatch({
    type: actionTypes.SET_RESCHEDULE_FLOW,
    payload: isRescheduleFlow,
  });
};

const setRescheduleReason =
  (dispatch: any) =>
  ({ reasonCode, reasonValue, description }: any) => {
    dispatch({
      type: actionTypes.SET_RESCHEDULE_REASON,
      payload: { reasonCode, reasonValue, description },
    });
  };

const setIsRouting = (dispatch: any) => (isRouting: true | '') => {
  dispatch({
    type: actionTypes.SET_IS_ROUTING,
    payload: isRouting,
  });
};

const resetService = (dispatch: any) => () => {
  dispatch({
    type: 'RESET',
  });
};

const setComprehensiveServiceData = (dispatch: any) => (data: any) => {
  dispatch({
    type: actionTypes.SET_COMPREHENSIVE_SERVICE_DATA,
    payload: data,
  });
};

const { Context: ServiceRequestContext, Provider: ServiceRequestProvider } = createContext<
  ServiceRequestContextType,
  ServiceRequestDispatchType
>(
  serviceRequestReducer,
  {
    changeComponent,
    backAction,
    updateServiceType,
    updateRepairType,
    updateServiceDate,
    updateServiceSlot,
    updateAddress,
    resetServiceAllocation,
    updateUseCurrentAddress,
    updateSRContext,
    updateSRHistoryContext,
    setServiceRequestID,
    initWebengageContext,
    setRescheduleFlow,
    setIsRouting,
    resetService,
    updateIncidentId,
    setRescheduleReason,
    setComprehensiveServiceData,
  },
  initialState,
);
ServiceRequestContext.displayName = 'ServiceRequest';

export const useServiceContext = () => useContext(ServiceRequestContext);

export { ServiceRequestContext, ServiceRequestProvider };
